import { Avatar, Flex, Skeleton, Typography } from 'antd';
import { Viewport } from 'components/ViewportProvider';
import { selectLoading, selectSections } from 'containers/HomePage/selectors';
import { ILoadingSection, ISection } from 'containers/HomePage/types';
import React, { FC, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import styled from 'styles/styled-components';
import { Mousewheel, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ApplicationRootState } from 'types';
import { commonDimensions } from 'utils/constants';
import { getInitials } from 'utils/getInitialsBrandName';
import utilsMessages from 'utils/messages';
import { generateUrlBrandDetail } from '../../utils/generateBrandDetailUrl';
import Arrow from 'components/HomeScreenWidget/Arrow';
import media from 'utils/mediaStyle';

const { Title } = Typography;

const Container = styled.div`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  margin: 0 auto;
  padding: 0 40px;
  @media (max-width: 1023px) {
    padding: 0 16px;
  }
  @media (max-width: 767px) {
    padding: 0;
  }
`;

const SectionTitle = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  margin-bottom: 16px;
  @media (max-width: 767px) {
    padding: 0 16px;
    align-items: center;
    margin-bottom: 12px;
  }
  h2 {
    margin-bottom: 0px;
  }
`;

const LearnMore = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #3c9041;
  &:hover {
    span {
      color: #3c9041;
      opacity: 0.8;
    }
    svg {
      opacity: 0.8;
    }
  }
`;

const SectionHeading = styled(Title)`
  &.ant-typography {
    font-size: 24px;
    line-height: 1.5;
  }
  @media (max-width: 991px) {
    &.ant-typography {
      font-size: 16px;
      line-height: 20px;
    }
  }
`;

const WidgetWrapper = styled('div')`
  display: grid;
  padding: 16px 0px;
  background-color: #fff;
  border-radius: 16px;
  margin: 0 auto 20px;
  box-shadow: 0 4px 24px 0 rgba(236, 236, 236, 0.8);
  @media (max-width: 576px) {
    border-radius: 0px;
    &:last-child {
      margin-bottom: 12px;
    }
    padding: 16px 0px 10px;
  }
`;

const List = styled('div')`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: hidden;
  padding: 1px;
`;

const Item = styled('div')`
  flex: 0 0 180px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  color: #595959;
`;
const ItemImg = styled(Avatar)`
  // flex: 1 1 0%;
  border-radius: 16px;
  border: 1px solid #eaebea;
  img {
    object-fit: contain;
  }
`;
const BrandName = styled('div')`
  flex: 1 1 0%;
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  font-weight: 500;
  color: #242527;
`;

const BrandButton = styled.div`
  width: 120px;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  // background: rgba(158, 158, 158, 0.1);
  @media (max-width: 767px) {
    width: 80px !important;
  }
`;
const BrandButtonName = styled.span`
  font-size: 14px;
  overflow: hidden;
  color: #111827;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  flex-grow: 1;
  text-align: center;
  @media (max-width: 767px) {
    font-size: 12px;
  }
`;
const SkeletonWrapper = styled.div`
  background: #ffffff;
  padding: 16px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const SwiperElement = styled(Swiper)`
  max-width: 1392px;
  margin: 0px 0px 0px !important;
  padding: 1px 20px !important;
  @media (max-width: 767px) {
    padding: 1px 16px !important;
  }
  .swiper-wrapper {
    padding-right: 0 !important;
  }
  .swiper-button-next,
  .swiper-button-prev {
    top: 80px !important;
    width: 40px !important;
    height: 40px !important;
    border-radius: 50% !important;
    background-color: #ffffff !important;
    box-shadow: 2px 6px 32px 0 rgba(0, 0, 0, 0.10);
    &:after {
      font-size: 14px !important;
      color: #596056 !important;
      font-weight: 700;
    }
    &.swiper-button-disabled {
      display: none !important;
    }
    @media (max-width: 767px) {
      display: none;
    }
  }
  .swiper-button-next {
    right: 4px;
    &:after {
      margin-left: 1px;
    }
  }
  .swiper-button-prev {
    left: 4px;
    &:after {
      margin-right: 1px;
    }
  }
  @media (max-width: 767px) {
    margin-bottom: 0px !important;
  }
`;

const SwiperSlideElement = styled(SwiperSlide)`
  width: 120px !important;
  border-radius: 8px;
  margin-right: 20px !important;
  &:last-child {
    margin-right: 0 !important;
  }
  @media (max-width: 767px) {
    width: 80px !important;
    margin-right: 12px !important;
  }
`;

interface IStateProps {
  sections: ISection;
  loading: ILoadingSection | boolean;
}

type Props = IStateProps;

const BrandWidget: FC<Props> = (props) => {
  const swiperRef: any = useRef(null);
  const { sections } = props;

  const brandsData = useMemo(() => {
    return (sections && sections?.TRUSTED_BRANDS?.data) || [];
  }, [sections]);

  const SkeletonLoading = () => {
    return (
      <>
        <Skeleton.Input active={true} style={{ marginBottom: '12px', height: '25px', width: '180px' }} />
        <Flex gap={16}>
          {/* {Array.from({ length: 16 }, (_, index) => index).map((item) => ( */}
          <Flex vertical align="center" justify="center">
            <Skeleton.Image active={true} style={{ marginBottom: '12px' }} />
            <Skeleton.Button active={true} style={{ height: '25px', width: '80px' }} />
          </Flex>
          <Flex vertical align="center" justify="center">
            <Skeleton.Image active={true} style={{ marginBottom: '12px' }} />
            <Skeleton.Button active={true} style={{ height: '25px', width: '80px' }} />
          </Flex>
          <Flex vertical align="center" justify="center">
            <Skeleton.Image active={true} style={{ marginBottom: '12px' }} />
            <Skeleton.Button active={true} style={{ height: '25px', width: '80px' }} />
          </Flex>
          <Flex vertical align="center" justify="center">
            <Skeleton.Image active={true} style={{ marginBottom: '12px' }} />
            <Skeleton.Button active={true} style={{ height: '25px', width: '80px' }} />
          </Flex>
          <Flex vertical align="center" justify="center">
            <Skeleton.Image active={true} style={{ marginBottom: '12px' }} />
            <Skeleton.Button active={true} style={{ height: '25px', width: '80px' }} />
          </Flex>
          <Flex vertical align="center" justify="center">
            <Skeleton.Image active={true} style={{ marginBottom: '12px' }} />
            <Skeleton.Button active={true} style={{ height: '25px', width: '80px' }} />
          </Flex>
          {/* ))} */}
        </Flex>
      </>
    );
  };

  return (
    <Viewport.Consumer>
      {({ width }) => {
        return (
          <>
            {!!brandsData?.length ? (
              <Container>
                <WidgetWrapper>
                  <SectionTitle id="trusted-brands">
                    <SectionHeading level={2}>
                      <FormattedMessage {...utilsMessages.trustedBrands} />
                    </SectionHeading>
                    <LearnMore to="/thuong-hieu">
                      <FormattedMessage {...utilsMessages[width > 991 ? 'seeAll' : 'seeAllMobile']} />
                      <Arrow color="#3c9041" />
                    </LearnMore>
                  </SectionTitle>
                  <List>
                    <SwiperElement
                      slidesPerView={'auto'}
                      navigation={true}
                      modules={[Navigation, Mousewheel]}
                      mousewheel={{ forceToAxis: true }}
                      simulateTouch={true}
                      className="mySwiper"
                      slidesPerGroup={2}
                      // slidesOffsetAfter={0}
                    >
                      {brandsData.map((item, index) => {
                        return (
                          <SwiperSlideElement key={index}>
                            <Link key={item.id} to={generateUrlBrandDetail(item.id, item?.name as string)}>
                              <Item key={item.id}>
                                {!item.imageUrl ? (
                                  <BrandName>{getInitials(item.name)}</BrandName>
                                ) : (
                                  <ItemImg size={width > 767 ? 121 : 81} src={item.imageUrl} />
                                )}
                                <BrandButton>
                                  <BrandButtonName>{item.name}</BrandButtonName>
                                </BrandButton>
                              </Item>
                            </Link>
                          </SwiperSlideElement>
                        );
                      })}
                    </SwiperElement>
                  </List>
                </WidgetWrapper>
              </Container>
            ) : (
              <Container className="container-padding">
                <SkeletonWrapper>
                  <SkeletonLoading />
                </SkeletonWrapper>
              </Container>
            )}
          </>
        );
      }}
    </Viewport.Consumer>
  );
};

const mapStateToProps = createStructuredSelector<ApplicationRootState, IStateProps>({
  sections: selectSections(),
  loading: selectLoading(),
});

const withConnect = connect<IStateProps>(mapStateToProps);

export default withConnect(BrandWidget);
